import type { ReactNode } from 'react';

import { Box, Typography } from '@mui/material';

import { GX_TILE_DARK, GX_TILE_LIGHT } from '@/constants/colors';

import type { ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import Loader from '@/shared/ui/feedback/Spinner';

import FullScreenLegend from './FullScreenLegend';
import useFullScreenChart from './useFullScreenChart';

const ChartStateFrame = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      width: '100%',
      minHeight: 420,
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column'
    }}
  >
    {children}
  </Box>
);

const FullScreenChart = ({
  chartSeries,
  error,
  loading,
  isLightMode,
  tabName
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  error: boolean;
  loading: boolean;
  isLightMode: boolean;
  tabName: string;
}) => {
  const hasSeries = Boolean(chartSeries?.length);
  const { containerRef, legendSeries, toggleSeriesVisibility } = useFullScreenChart({
    chartId: tabName,
    chartSeries: chartSeries ?? []
  });

  if (error) {
    return (
      <ChartStateFrame>
        <Typography color='info'>
          We couldn't retrieve your data. Please try again later.
        </Typography>
      </ChartStateFrame>
    );
  }

  if (loading || !chartSeries) {
    return (
      <ChartStateFrame>
        <Loader />
      </ChartStateFrame>
    );
  }

  if (!hasSeries) {
    return (
      <ChartStateFrame>
        <EmptyState />
      </ChartStateFrame>
    );
  }

  return (
    <Box
      className={`gx-fullscreen-stock-chart ${isLightMode ? 'highcharts-light' : 'highcharts-dark'}`}
      sx={theme => {
        const gxPalette = theme.palette.gx as { tileDark?: string; tileLight?: string } | undefined;

        return {
          '--gx-fullscreen-tile-dark': gxPalette?.tileDark ?? GX_TILE_DARK,
          '--gx-fullscreen-tile-light': gxPalette?.tileLight ?? GX_TILE_LIGHT,
          width: '100%',
          minHeight: 0
        };
      }}
    >
      <Box
        ref={containerRef}
        aria-label={`${tabName} stock chart`}
        data-testid='fullscreen-stock-chart'
        sx={{
          width: '100%',
          height: 'calc(100vh - 330px)',
          minHeight: 420,
          backgroundColor: 'var(--highcharts-background-color)',
          color: 'var(--highcharts-neutral-color-80)'
        }}
      />
      <FullScreenLegend
        series={legendSeries}
        onToggleSeries={toggleSeriesVisibility}
      />
    </Box>
  );
};

export default FullScreenChart;
